import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { recordIdentifierFor } from '@warp-drive/core';
import can from 'ember-can/helpers/can';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import { eq, not } from 'ember-truth-helpers';
import { FreeMode, Mousewheel, Scrollbar } from 'swiper/modules';

import Button from 'umg-ember-backend/components/ui/button';
import ContextMenu from 'umg-ember-backend/components/ui/context-menu';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import Page from './page';
import Preview from './preview';
import previewStyles from './preview/styles.module.css';
import styles from './styles.module.css';
import swiper from './swiper-modifier';

const bem = getBem(styles);

export default class PageBuilderPages extends Component {
  // Services
  @service breadcrumb;
  @service confirm;
  @service flashMessages;
  @service history;
  @service intl;
  @service recordCopy;
  @service recordStatus;
  @service store;

  // Defaults
  @storeOnRoute('routeStorageKey') selectedPageToEdit = null;
  @storeOnRoute('routeStorageKey') scrollPositionPage;
  @tracked selectedPageType = 'page-builder-page';
  @tracked isDragging = false;

  // Getter and setter
  get routeStorageKey() {
    return recordIdentifierFor(this.args.mlp).lid;
  }

  get filteredPages() {
    return this.args.mlp.pages.slice().filter((page) => {
      switch (this.selectedPageType) {
        case 'page-builder-page':
          return ['internal', 'external'].includes(page.linkType);
        case 'artist-page':
          return ['artist'].includes(page.linkType);
      }
    });
  }

  get sortedPages() {
    return this.filteredPages.sort((a, b) => a.position - b.position);
  }

  get isDisabled() {
    return this.args.isDisabled ?? this.recordStatus.isDisabled(this.args.mlp);
  }

  initialSwiperOptions = {
    modules: [FreeMode, Mousewheel, Scrollbar],
    slidesPerView: 'auto',
    freeMode: {
      enabled: true,
    },
    spaceBetween: 10,
    shortSwipes: false,
    touchStartPreventDefault: false,

    mousewheel: true,

    scrollbar: {
      el: styles['scrollbar'],
      draggable: true,
      lockClass: styles['scrollbar--is-locked'],
      dragClass: styles['drag'],
    },

    // Classes
    wrapperClass: styles['list'],
    slideClass: previewStyles['scope'],
    slideActiveClass: previewStyles['scope--is-active'],
    slideDuplicatedActiveClass: previewStyles['scope--duplicated-active'],
    slideVisibleClass: previewStyles['scope--is-visible'],
    slideDuplicateClass: previewStyles['scope--is-duplicatd'],
    slideNextClass: previewStyles['scope--is-next'],
    slideDuplicatedNextClass: previewStyles['scope--duplicated-next'],
    slidePrevClass: previewStyles['scope--is-prev'],
    slideDuplicatedPrevClass: previewStyles['scope--duplicated-prev'],
  };

  // Functions
  addPage = () => {
    const linkType =
      this.selectedPageType === 'artist-page' ? 'artist' : 'internal';
    let page = this.store.createRecord('mlp-page', {
      position: this.filteredPages.length,
      linkType: linkType,
    });

    this.args.mlp.pages.push(page);
    this.selectedPageToEdit = page;
  };

  closePage = () => {
    this.selectedPageToEdit = null;
    this.scrollPositionPage = 0;
  };

  createStartPage = () => {
    const hasStartPage = this.args.mlp.pages.find((page) => page.start);
    const isNew = this.args.mlp.isNew;

    if (isNew && !hasStartPage) {
      let startPage = this.store.createRecord('mlp-page', {
        name: 'Start',
        linkType: 'internal',
        link: '/',
        visible: false,
        start: true,
      });

      this.args.mlp.pages.push(startPage);
    }
  };

  duplicatePage = async (pages, page) => {
    let currentPosition = pages.indexOf(page);

    for (let i = currentPosition + 1; i <= pages.length - 1; i++) {
      pages.at(i).set('position', i + 1);
    }

    await this.recordCopy.copy(page, {
      ignoreAttributes: ['start'],
      overwrite: {
        position: currentPosition + 1,
      },
    });
  };

  editPage = (page) => {
    this.selectedPageToEdit = page;
  };

  removePage = async (page) => {
    let ok = await this.history.confirmAction('delete', page);

    if (!ok) {
      return;
    }

    const index = this.args.mlp.pages.indexOf(page);
    if (index !== -1) {
      this.args.mlp.pages.splice(index, 1);
    }
    this.args.onSelectPage(this.sortedPages[0]);

    page.deleteRecord();
    await this.args.onSaveMlp();
  };

  reorderPages = (pages) => {
    pages.forEach((page, index) => {
      page.position = index;
    });
  };

  savePage = async () => {
    const { isNew, name, linkType } = this.selectedPageToEdit;
    await this.args.onSaveMlp();

    if (isNew) {
      const probablyNewPage = this.args.mlp.pages.find((page) => {
        return page.name === name && page.linkType === linkType;
      });

      this.selectedPageToEdit = probablyNewPage;
    }

    this.args.onSelectPage(this.selectedPageToEdit);
  };

  // Template
  <template>
    <div class={{(bem)}} {{didInsert this.createStartPage}} ...attributes>
      <div
        class={{bem 'slider'}}
        {{swiper
          items=this.sortedPages
          initialOptions=this.initialSwiperOptions
          trackedOptions=(hash allowTouchMove=(not this.isDragging))
        }}
      >
        <div
          class={{bem 'list'}}
          {{sortableGroup
            groupName='mlp-pages'
            onChange=this.reorderPages
            direction='x'
          }}
        >
          {{#each this.sortedPages as |page|}}
            <Preview
              @sortableGroup='mlp-pages'
              @isDisabled={{this.isDisabled}}
              @onDragEnd={{fn (mut this.isDragging) false}}
              @onDragStart={{fn (mut this.isDragging) true}}
              @onDuplicatePage={{fn this.duplicatePage @mlp.pages}}
              @onEditPage={{this.editPage}}
              @onRemovePage={{this.removePage}}
              @onSelectPage={{@onSelectPage}}
              @page={{page}}
              @selectedPage={{@selectedPage}}
              @showErrors={{@showErrors}}
            />
          {{/each}}
        </div>

        <div class={{bem 'scrollbar'}}></div>
      </div>
      <div class={{bem 'pages-actions'}}>
        {{#if (can 'create' 'mlp-page')}}
          <Button
            @style='primary'
            @disabled={{this.isDisabled}}
            @icon='add'
            @tooltip='Navigationspunkt hinzufügen'
            class={{bem 'action'}}
            {{on 'click' this.addPage}}
          />
        {{/if}}
        {{#if @mlp.isV2}}
          <ContextMenu
            @isDisabled={{this.isDisabled}}
            @horizontalPosition='auto-right'
          >
            <:trigger>
              <Button
                @style='grey'
                @disabled={{this.isDisabled}}
                @icon={{if
                  (eq this.selectedPageType 'artist-page')
                  'artist-page'
                  'preview'
                }}
                class={{bem 'action'}}
              />
            </:trigger>

            <:content as |content|>
              <content.Item
                @icon='artist-page'
                @label='Artist Seiten'
                @isActive={{eq this.selectedPageType 'artist-page'}}
                @onClick={{fn (mut this.selectedPageType) 'artist-page'}}
              />

              <content.Item
                @icon='preview'
                @label='Page Builder Seiten'
                @isActive={{eq this.selectedPageType 'page-builder-page'}}
                @onClick={{fn (mut this.selectedPageType) 'page-builder-page'}}
              />
            </:content>
          </ContextMenu>
        {{/if}}
      </div>

      <Page
        @onBodyScroll={{fn (mut this.scrollPositionPage)}}
        @onClosePage={{this.closePage}}
        @onSavePage={{this.savePage}}
        @page={{this.selectedPageToEdit}}
        @scrollPosition={{this.scrollPositionPage}}
      />
    </div>
    {{#if @showErrors}}
      <div class={{bem 'error'}}>
        {{@mlp.validations.attrs.pages.message}}
      </div>
    {{/if}}
  </template>
}
